import { useEffect, useMemo, useState } from "react";
import {
  Plus, Edit2, Trash2, Loader2, GripVertical, Star, Save, LayoutList, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel,
} from "@/components/ui/select";

const FIELD_TYPES = [
  { v: "text", label: "Text" },
  { v: "textarea", label: "Long text" },
  { v: "number", label: "Number" },
  { v: "currency", label: "Currency" },
  { v: "dropdown", label: "Dropdown" },
  { v: "date", label: "Date (calendar)" },
  { v: "checkbox", label: "Checkbox" },
  { v: "phone", label: "Phone (validated)" },
  { v: "email", label: "Email (validated)" },
];

const STANDARD_BINDINGS = [
  { v: "first_name", label: "First Name" },
  { v: "last_name", label: "Last Name" },
  { v: "full_name", label: "Full Name" },
  { v: "phone", label: "Phone" },
  { v: "email", label: "Email" },
  { v: "company", label: "Company" },
  { v: "designation", label: "Designation" },
  { v: "status", label: "Status" },
  { v: "address", label: "Address" },
  { v: "city", label: "City" },
  { v: "state", label: "State" },
  { v: "pincode", label: "Pincode" },
  { v: "disposition", label: "Disposition" },
];

const HANDLE_BINDINGS = [
  { v: "handle:whatsapp", label: "WhatsApp number / ID" },
  { v: "handle:instagram", label: "Instagram ID" },
  { v: "handle:facebook", label: "Facebook PSID" },
  { v: "handle:telegram", label: "Telegram ID" },
  { v: "handle:viber", label: "Viber user ID" },
  { v: "handle:webchat", label: "Webchat ID" },
];

const CURRENCIES = ["USD", "INR", "EUR", "GBP", "AED", "AUD", "CAD", "SGD"];

const NONE = "__none__";
function bindingLabel(b) {
  if (!b) return "Custom field";
  return [...STANDARD_BINDINGS, ...HANDLE_BINDINGS].find((x) => x.v === b)?.label || b;
}
function slug(s) { return (s || "").toLowerCase().replace(/[^a-z0-9_]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, ""); }

export default function CrmScreensPage() {
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);          // working copy of selected screen
  const [saving, setSaving] = useState(false);
  const [fieldEditor, setFieldEditor] = useState(null); // { index|null, data }
  const [dragIdx, setDragIdx] = useState(null);
  const [newScreenOpen, setNewScreenOpen] = useState(false);
  const [newScreenName, setNewScreenName] = useState("");
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load(selectId) {
    setLoading(true);
    try {
      const r = await api.get("/admin/crm-screens");
      const rows = r.data || [];
      setScreens(rows);
      const pick = selectId || selId || rows[0]?.id;
      const s = rows.find((x) => x.id === pick) || rows[0];
      setSelId(s?.id || null);
      setDraft(s ? JSON.parse(JSON.stringify(s)) : null);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function selectScreen(id) {
    const s = screens.find((x) => x.id === id);
    setSelId(id);
    setDraft(s ? JSON.parse(JSON.stringify(s)) : null);
  }

  async function createScreen() {
    const name = newScreenName.trim();
    if (!name) { toast.error("Name required"); return; }
    try {
      const { data } = await api.post("/admin/crm-screens", { name, fields: [] });
      setNewScreenOpen(false); setNewScreenName("");
      await load(data.id);
      toast.success("Screen created");
    } catch (e) { toast.error(formatErr(e)); }
  }

  async function saveScreen() {
    if (!draft) return;
    setSaving(true);
    try {
      await api.patch(`/admin/crm-screens/${draft.id}`, {
        name: draft.name, description: draft.description || "",
        is_default: !!draft.is_default, fields: draft.fields,
      });
      toast.success("Screen saved");
      await load(draft.id);
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }

  async function makeDefault() {
    if (!draft) return;
    try { await api.patch(`/admin/crm-screens/${draft.id}`, { is_default: true }); await load(draft.id); toast.success("Set as default"); }
    catch (e) { toast.error(formatErr(e)); }
  }

  async function deleteScreen() {
    if (!draft) return;
    if (!confirm(`Delete screen "${draft.name}"? Campaigns using it will fall back to the default.`)) return;
    try { await api.delete(`/admin/crm-screens/${draft.id}`); toast.success("Deleted"); setSelId(null); await load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  // ── field editor ──────────────────────────────────────────────────────
  function openNewField() {
    setFieldEditor({ index: null, data: { key: "", label: "", type: "text", required: false, options: [], binding: "", currency_code: "USD", placeholder: "", help: "" } });
  }
  function openEditField(i) { setFieldEditor({ index: i, data: JSON.parse(JSON.stringify(draft.fields[i])) }); }
  function commitField() {
    const d = fieldEditor.data;
    const label = (d.label || "").trim();
    if (!label) { toast.error("Label required"); return; }
    const key = slug(d.key) || slug(d.binding?.replace("handle:", "")) || slug(label);
    const field = { ...d, label, key };
    const others = draft.fields.filter((_, i) => i !== fieldEditor.index);
    if (others.some((f) => f.key === key)) { toast.error(`Key "${key}" already used in this screen`); return; }
    const next = [...draft.fields];
    if (fieldEditor.index == null) next.push(field);
    else next[fieldEditor.index] = field;
    setDraft({ ...draft, fields: next });
    setFieldEditor(null);
  }
  function removeField(i) { setDraft({ ...draft, fields: draft.fields.filter((_, x) => x !== i) }); }

  // ── drag & drop reorder ───────────────────────────────────────────────
  function onDrop(target) {
    if (dragIdx == null || dragIdx === target) { setDragIdx(null); return; }
    const next = [...draft.fields];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(target, 0, moved);
    setDraft({ ...draft, fields: next });
    setDragIdx(null);
  }

  const dirty = useMemo(() => {
    const orig = screens.find((s) => s.id === selId);
    return orig && draft && JSON.stringify({ n: orig.name, d: orig.description, f: orig.fields, def: orig.is_default })
      !== JSON.stringify({ n: draft.name, d: draft.description, f: draft.fields, def: draft.is_default });
  }, [screens, draft, selId]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="crm-screens-title">CRM Screens</h1>
          <p className="text-sm text-muted-foreground mt-1">Build the fields shown in the agent lead popup, then assign a screen to a campaign. Fields can bind to real contact attributes or omnichannel IDs, or store fully custom data.</p>
        </div>
        {canWrite && <Button data-testid="new-screen-btn" onClick={() => setNewScreenOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Plus className="w-4 h-4" /> New screen</Button>}
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
          {/* Screen list */}
          <Card className="p-2 h-fit">
            <div className="text-overline text-muted-foreground px-2 py-1.5">Screens ({screens.length})</div>
            <ul className="space-y-1" data-testid="screen-list">
              {screens.map((s) => (
                <li key={s.id}>
                  <button
                    data-testid={`screen-item-${s.id}`}
                    onClick={() => selectScreen(s.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${selId === s.id ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted"}`}
                  >
                    <LayoutList className="w-4 h-4 shrink-0" />
                    <span className="truncate flex-1">{s.name}</span>
                    {s.is_default && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 shrink-0" />}
                    <Badge variant="outline" className="text-[10px] shrink-0">{(s.fields || []).length}</Badge>
                  </button>
                </li>
              ))}
              {screens.length === 0 && <li className="text-xs text-muted-foreground px-3 py-2">No screens yet.</li>}
            </ul>
          </Card>

          {/* Editor */}
          {draft ? (
            <Card className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex-1 min-w-[240px] space-y-2">
                  <Input data-testid="screen-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="font-display font-bold text-lg h-11" placeholder="Screen name" />
                  <Input data-testid="screen-desc" value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="h-9 text-sm" placeholder="Description (optional)" />
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {draft.is_default
                    ? <Badge className="gap-1 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"><Star className="w-3 h-3 fill-current" /> Default</Badge>
                    : (canWrite && <Button data-testid="make-default-btn" variant="outline" size="sm" onClick={makeDefault} className="gap-1.5"><Star className="w-3.5 h-3.5" /> Set default</Button>)}
                  {canDelete && <Button data-testid="delete-screen-btn" variant="outline" size="sm" onClick={deleteScreen} className="text-destructive gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</Button>}
                  {canWrite && <Button data-testid="save-screen-btn" size="sm" onClick={saveScreen} disabled={saving || !dirty} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</Button>}
                </div>
              </div>

              <div className="flex items-center justify-between mb-2">
                <div className="text-overline text-muted-foreground">Fields — drag to reorder</div>
                {canWrite && <Button data-testid="add-field-btn" variant="outline" size="sm" onClick={openNewField} className="gap-1.5"><Plus className="w-3.5 h-3.5" /> Add field</Button>}
              </div>

              <ul className="space-y-2" data-testid="field-list">
                {draft.fields.map((f, i) => (
                  <li
                    key={f.key + i}
                    draggable
                    onDragStart={() => setDragIdx(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(i)}
                    data-testid={`field-row-${f.key}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-card hover:border-primary/40 transition-colors ${dragIdx === i ? "opacity-40" : ""}`}
                  >
                    <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{f.label}</span>
                        {f.required && <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-600 dark:text-rose-400">required</Badge>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <code className="text-[11px] text-muted-foreground">{f.key}</code>
                        <Badge variant="secondary" className="text-[10px] uppercase">{f.type}</Badge>
                        <Badge variant="outline" className={`text-[10px] ${f.binding ? "border-emerald-300 text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`}>{bindingLabel(f.binding)}</Badge>
                      </div>
                    </div>
                    <Button data-testid={`edit-field-${f.key}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditField(i)}><Edit2 className="w-4 h-4" /></Button>
                    <Button data-testid={`del-field-${f.key}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeField(i)}><Trash2 className="w-4 h-4" /></Button>
                  </li>
                ))}
                {draft.fields.length === 0 && <li className="text-center text-sm text-muted-foreground py-10 border border-dashed rounded-lg">No fields yet — click “Add field”.</li>}
              </ul>
            </Card>
          ) : (
            <Card className="p-10 text-center text-sm text-muted-foreground">Select or create a screen to start building.</Card>
          )}
        </div>
      )}

      {/* New screen dialog */}
      <Dialog open={newScreenOpen} onOpenChange={setNewScreenOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New CRM screen</DialogTitle><DialogDescription>Give the screen a name. You can add fields after it’s created.</DialogDescription></DialogHeader>
          <div className="py-2"><Label>Name</Label><Input data-testid="new-screen-name" value={newScreenName} onChange={(e) => setNewScreenName(e.target.value)} placeholder="e.g. Sales — Outbound" onKeyDown={(e) => e.key === "Enter" && createScreen()} autoFocus /></div>
          <DialogFooter><Button variant="outline" onClick={() => setNewScreenOpen(false)}>Cancel</Button><Button data-testid="create-screen-btn" onClick={createScreen} className="bg-primary text-primary-foreground">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field editor dialog */}
      <FieldEditorDialog editor={fieldEditor} setEditor={setFieldEditor} onCommit={commitField} />
    </div>
  );
}

function FieldEditorDialog({ editor, setEditor, onCommit }) {
  if (!editor) return null;
  const d = editor.data;
  const set = (patch) => setEditor({ ...editor, data: { ...editor.data, ...patch } });
  return (
    <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editor.index == null ? "Add field" : "Edit field"}</DialogTitle><DialogDescription>Define the label, type and where the value is stored.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3 py-1">
          <div className="sm:col-span-2"><Label>Label</Label><Input data-testid="fe-label" value={d.label} onChange={(e) => set({ label: e.target.value, key: editor.index == null && !d._keyTouched ? slug(e.target.value) : d.key })} placeholder="e.g. Instagram Handle" /></div>
          <div><Label>Key</Label><Input data-testid="fe-key" value={d.key} onChange={(e) => set({ key: slug(e.target.value), _keyTouched: true })} placeholder="auto" /></div>
          <div><Label>Type</Label>
            <Select value={d.type} onValueChange={(v) => set({ type: v })}>
              <SelectTrigger data-testid="fe-type" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Binding (where the value is saved)</Label>
            <Select value={d.binding || NONE} onValueChange={(v) => set({ binding: v === NONE ? "" : v })}>
              <SelectTrigger data-testid="fe-binding" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Custom field (stored on contact)</SelectItem>
                <SelectGroup><SelectLabel>Contact attribute</SelectLabel>{STANDARD_BINDINGS.map((b) => <SelectItem key={b.v} value={b.v}>{b.label}</SelectItem>)}</SelectGroup>
                <SelectGroup><SelectLabel>Omnichannel ID</SelectLabel>{HANDLE_BINDINGS.map((b) => <SelectItem key={b.v} value={b.v}>{b.label}</SelectItem>)}</SelectGroup>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">Voice & SMS use the <b>Phone</b> field. Omnichannel IDs feed the in-call DM history.</p>
          </div>
          {d.type === "dropdown" && (
            <div className="sm:col-span-2"><Label>Options (comma-separated)</Label>
              <Input data-testid="fe-options" value={Array.isArray(d.options) ? d.options.join(", ") : ""} onChange={(e) => set({ options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Option A, Option B" /></div>
          )}
          {d.type === "currency" && (
            <div><Label>Currency</Label>
              <Select value={d.currency_code || "USD"} onValueChange={(v) => set({ currency_code: v })}>
                <SelectTrigger data-testid="fe-currency" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div className="flex items-center gap-2 sm:col-span-2 mt-1"><Switch data-testid="fe-required" checked={!!d.required} onCheckedChange={(v) => set({ required: !!v })} /><Label>Required</Label></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button><Button data-testid="fe-save" onClick={onCommit} className="bg-primary text-primary-foreground">{editor.index == null ? "Add" : "Update"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
